import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const formatDate = (date) => date.toISOString().split('T')[0];

const usePhotoFilters = ({
  provider,
  loadingPreferences,
  loadingFreeTrial,
  onFetchSuccess
}) => {
  const [albums, setAlbums] = useState([]);
  const [fetchingAlbums, setFetchingAlbums] = useState(true);
  const [selectedAlbum, setSelectedAlbum] = useState('');
  const [tags, setTags] = useState('');
  const [uploadStart, setUploadStart] = useState('');
  const [uploadEnd, setUploadEnd] = useState('');
  const [takenStart, setTakenStart] = useState('');
  const [takenEnd, setTakenEnd] = useState('');
  const [photos, setPhotos] = useState([]);
  const [selectedPhotos, setSelectedPhotos] = useState(new Set());
  const [loading, setLoading] = useState(false);

  const notifyFetchSuccess = useCallback(
    (newPhotos, options = {}) => {
      if (onFetchSuccess) {
        onFetchSuccess({ photos: newPhotos, ...options });
      }
    },
    [onFetchSuccess]
  );

  const getDateRange = useCallback((range) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (range) {
      case 'today': {
        const value = formatDate(today);
        return { start: value, end: value };
      }
      case 'yesterday': {
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        const value = formatDate(yesterday);
        return { start: value, end: value };
      }
      case 'this-week': {
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        return { start: formatDate(startOfWeek), end: formatDate(today) };
      }
      case 'last-week': {
        const startOfLastWeek = new Date(today);
        startOfLastWeek.setDate(today.getDate() - today.getDay() - 7);
        const endOfLastWeek = new Date(startOfLastWeek);
        endOfLastWeek.setDate(startOfLastWeek.getDate() + 6);
        return {
          start: formatDate(startOfLastWeek),
          end: formatDate(endOfLastWeek)
        };
      }
      case 'this-month': {
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        return { start: formatDate(startOfMonth), end: formatDate(today) };
      }
      case 'last-month': {
        const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        return {
          start: formatDate(startOfLastMonth),
          end: formatDate(endOfLastMonth)
        };
      }
      case 'this-year': {
        const startOfYear = new Date(today.getFullYear(), 0, 1);
        return { start: formatDate(startOfYear), end: formatDate(today) };
      }
      case 'last-year': {
        const startOfLastYear = new Date(today.getFullYear() - 1, 0, 1);
        const endOfLastYear = new Date(today.getFullYear() - 1, 11, 31);
        return {
          start: formatDate(startOfLastYear),
          end: formatDate(endOfLastYear)
        };
      }
      case 'last-30-days': {
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(today.getDate() - 30);
        return { start: formatDate(thirtyDaysAgo), end: formatDate(today) };
      }
      case 'last-90-days': {
        const ninetyDaysAgo = new Date(today);
        ninetyDaysAgo.setDate(today.getDate() - 90);
        return { start: formatDate(ninetyDaysAgo), end: formatDate(today) };
      }
      default:
        return { start: '', end: '' };
    }
  }, []);

  const fetchAlbums = useCallback(async () => {
    if (!provider) {
      setFetchingAlbums(false);
      setAlbums([]);
      return;
    }

    setFetchingAlbums(true);
    try {
      const response = await axios.get('/api/photos/albums', {
        params: { provider },
        withCredentials: true
      });
      setAlbums(response.data.albums);
    } catch (error) {
      toast.error(`Failed to load ${provider} albums`);
    } finally {
      setFetchingAlbums(false);
    }
  }, [provider]);

  useEffect(() => {
    fetchAlbums();
  }, [fetchAlbums]);

  const fetchPhotos = useCallback(
    async (options = { collapseCriteria: true }) => {
      if (fetchingAlbums || loadingPreferences || loadingFreeTrial || !provider) {
        return false;
      }

      setLoading(true);
      try {
        const params = { provider };
        if (selectedAlbum) params.albums = selectedAlbum;
        if (tags.trim()) params.tags = tags.trim();
        if (uploadStart) params.dateUploadedStart = uploadStart;
        if (uploadEnd) params.dateUploadedEnd = uploadEnd;
        if (takenStart) params.dateTakenStart = takenStart;
        if (takenEnd) params.dateTakenEnd = takenEnd;

        const response = await axios.get('/api/photos', {
          params,
          withCredentials: true
        });

        const fetchedPhotos = response.data.photos;
        setPhotos(fetchedPhotos);
        setSelectedPhotos(new Set());
        notifyFetchSuccess(fetchedPhotos, options);
        return true;
      } catch (error) {
        console.error('Failed to fetch photos:', error);
        if (
          selectedAlbum ||
          tags.trim() ||
          uploadStart ||
          uploadEnd ||
          takenStart ||
          takenEnd
        ) {
          toast.error(`Failed to fetch ${provider} photos`);
        }
        return false;
      } finally {
        setLoading(false);
      }
    },
    [
      fetchingAlbums,
      loadingPreferences,
      loadingFreeTrial,
      provider,
      selectedAlbum,
      tags,
      uploadStart,
      uploadEnd,
      takenStart,
      takenEnd,
      notifyFetchSuccess
    ]
  );

  const handleAlbumSelect = useCallback((albumId) => {
    setSelectedAlbum(albumId);
  }, []);

  const handleDateChange = useCallback((value, type) => {
    if (type === 'uploadStart') setUploadStart(value);
    if (type === 'uploadEnd') setUploadEnd(value);
    if (type === 'takenStart') setTakenStart(value);
    if (type === 'takenEnd') setTakenEnd(value);
  }, []);

  const handleTagSearch = useCallback(() => {
    if (tags.trim()) {
      fetchPhotos({ collapseCriteria: true });
    }
  }, [fetchPhotos, tags]);

  const clearDateRange = useCallback((type) => {
    if (type === 'upload') {
      setUploadStart('');
      setUploadEnd('');
    } else if (type === 'taken') {
      setTakenStart('');
      setTakenEnd('');
    }
  }, []);

  const applyDateRange = useCallback(
    (range, type) => {
      const { start, end } = getDateRange(range);

      if (type === 'upload') {
        setUploadStart(start);
        setUploadEnd(end);
      } else if (type === 'taken') {
        setTakenStart(start);
        setTakenEnd(end);
      }
    },
    [getDateRange]
  );

  const togglePhoto = useCallback((id) => {
    setSelectedPhotos((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    let shouldScroll = false;
    setSelectedPhotos((current) => {
      if (current.size === photos.length) {
        return new Set();
      }

      shouldScroll = photos.length > 0;
      return new Set(photos.map((photo) => photo.id));
    });
    return shouldScroll;
  }, [photos]);

  const selectUntitledPhotos = useCallback(() => {
    const untitledPhotos = photos.filter(
      (photo) => !photo.title || photo.title.trim() === ''
    );

    if (untitledPhotos.length === 0) {
      return false;
    }

    setSelectedPhotos((current) => {
      const next = new Set(current);
      untitledPhotos.forEach((photo) => next.add(photo.id));
      return next;
    });

    return true;
  }, [photos]);

  const handleQuickAction = useCallback(
    async (actionType) => {
      if (!provider) {
        return null;
      }

      setLoading(true);

      setSelectedAlbum('');
      setTags('');
      setUploadStart('');
      setUploadEnd('');
      setTakenStart('');
      setTakenEnd('');

      try {
        let params = {};
        let actionDescription = '';

        switch (actionType) {
          case 'uploaded-today': {
            const { start, end } = getDateRange('today');
            params = { dateUploadedStart: start, dateUploadedEnd: end };
            actionDescription = 'Photos Uploaded Today';
            setUploadStart(start);
            setUploadEnd(end);
            break;
          }
          case 'this-week': {
            const { start, end } = getDateRange('this-week');
            params = { dateUploadedStart: start, dateUploadedEnd: end };
            actionDescription = 'Photos from This Week';
            setUploadStart(start);
            setUploadEnd(end);
            break;
          }
          case 'recent-uploads': {
            const { start } = getDateRange('last-30-days');
            params = {
              dateUploadedStart: start,
              limit: 50,
              sort: 'dateUploaded',
              order: 'desc'
            };
            actionDescription = 'Recent Uploads (Last 30 Days)';
            setUploadStart(start);
            setUploadEnd(formatDate(new Date()));
            break;
          }
          default:
            return null;
        }

        const response = await axios.get('/api/photos', {
          params: { ...params, provider },
          withCredentials: true
        });

        const actionPhotos = response.data.photos || [];
        setPhotos(actionPhotos);
        setSelectedPhotos(new Set());
        notifyFetchSuccess(actionPhotos, { collapseCriteria: true });
        toast.success(`Loaded ${actionPhotos.length} photos: ${actionDescription}`);
        return actionPhotos.length;
      } catch (error) {
        console.error(`Failed to fetch photos for quick action:`, error);
        toast.error('Failed to load photos');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [getDateRange, notifyFetchSuccess, provider]
  );

  useEffect(() => {
    if (
      selectedAlbum &&
      !fetchingAlbums &&
      !loadingPreferences &&
      !loadingFreeTrial
    ) {
      fetchPhotos({ collapseCriteria: true });
    }
  }, [
    selectedAlbum,
    fetchingAlbums,
    loadingPreferences,
    loadingFreeTrial,
    fetchPhotos
  ]);

  useEffect(() => {
    if (
      uploadStart &&
      uploadEnd &&
      !fetchingAlbums &&
      !loadingPreferences &&
      !loadingFreeTrial
    ) {
      fetchPhotos({ collapseCriteria: true });
    }
  }, [
    uploadStart,
    uploadEnd,
    fetchingAlbums,
    loadingPreferences,
    loadingFreeTrial,
    fetchPhotos
  ]);

  useEffect(() => {
    if (
      takenStart &&
      takenEnd &&
      !fetchingAlbums &&
      !loadingPreferences &&
      !loadingFreeTrial
    ) {
      fetchPhotos({ collapseCriteria: true });
    }
  }, [
    takenStart,
    takenEnd,
    fetchingAlbums,
    loadingPreferences,
    loadingFreeTrial,
    fetchPhotos
  ]);

  const getFeaturedAlbums = useCallback(() => {
    if (!albums || albums.length === 0) {
      return [];
    }

    const sortedByDate = [...albums].sort(
      (a, b) => new Date(b.dateCreated || 0) - new Date(a.dateCreated || 0)
    );
    const sortedByViews = [...albums].sort(
      (a, b) => (b.views || 0) - (a.views || 0)
    );
    const sortedByPhotoCount = [...albums].sort(
      (a, b) => (b.photoCount || 0) - (a.photoCount || 0)
    );

    const featured = [];

    if (sortedByDate[0]) {
      featured.push({
        ...sortedByDate[0],
        featuredType: 'recent',
        featuredTitle: 'Most Recent Album',
        featuredDescription: `${sortedByDate[0].photoCount} photos`
      });
    }

    const popularAlbum = sortedByViews[0] || sortedByPhotoCount[0];
    if (popularAlbum && popularAlbum.id !== sortedByDate[0]?.id) {
      featured.push({
        ...popularAlbum,
        featuredType: 'popular',
        featuredTitle: 'Most Popular Album',
        featuredDescription: `${popularAlbum.photoCount} photos`
      });
    }

    const remaining = albums.filter(
      (album) =>
        album.id !== sortedByDate[0]?.id &&
        album.id !== popularAlbum?.id &&
        album.photoCount > 0
    );

    if (remaining.length > 0) {
      const randomAlbum = remaining[Math.floor(Math.random() * remaining.length)];
      featured.push({
        ...randomAlbum,
        featuredType: 'random',
        featuredTitle: 'AI Pick',
        featuredDescription: `${randomAlbum.photoCount} photos • Surprise selection`
      });
    }

    return featured.slice(0, 3);
  }, [albums]);

  const fetchPhotosWithCollapse = useCallback(
    () => fetchPhotos({ collapseCriteria: true }),
    [fetchPhotos]
  );

  return {
    albums,
    fetchingAlbums,
    selectedAlbum,
    setSelectedAlbum,
    getFeaturedAlbums,
    handleAlbumSelect,
    handleQuickAction,
    tags,
    setTags,
    handleTagSearch,
    uploadStart,
    uploadEnd,
    takenStart,
    takenEnd,
    handleDateChange,
    applyDateRange,
    clearDateRange,
    photos,
    loading,
    fetchPhotos: fetchPhotosWithCollapse,
    selectedPhotos,
    togglePhoto,
    toggleSelectAll,
    selectUntitledPhotos
  };
};

export default usePhotoFilters;
